'use client'

import classnames from 'classnames'
import styles from './ContentWithStickyNavigation.module.scss'
import { useContext, useEffect, useRef, useState } from 'react'
import { ScrollContext } from '@/context/Scroll'
import useI18n from '@/hooks/use-i18n'
import useWindowResize from '@/hooks/use-window-resize'
import { BREAKPOINTS } from '@/hooks/use-breakpoint'
import useStore from '@/store'
import { NAV_ID } from '@/components/Navigation/Navigation'

export type ContentWithStickyNavigationLineItem = {
  title: string
  id: string
  count?: number
}

// Resolve the sticky-header chrome height (header + open banner + mobile sticky button bar) into actual pixels.
//
// Reads the rendered <header id={NAV_ID}> element's bottom edge directly rather than the
// --header-height CSS variable: the variable is a static design-token value that can drift
// from the rendered header (e.g. when the header has internal padding/border, or its
// breakpoint-specific layout differs from the var). The sticky banner is rendered INSIDE the
// header when open, so the header's rendered bottom already accounts for it — measuring
// `bottom` (rather than `height`) also stays correct if a future layout pushes the header down
// instead of nesting the banner inside it. Do NOT add the banner height separately or it gets
// double-counted (which left the clicked category title peeking below the sticky bar whenever
// the banner was open).
//
// Split from per-element callers so hot paths (scroll-tick handler) can compute the value
// once per [stickyBannerIsOpen, resizeKey] window and avoid the layout work on every frame.
export const measureStickyChromeHeight = (): number => {
  if (typeof document === 'undefined') return 0

  const headerEl = document.getElementById(NAV_ID)
  const headerBottom = headerEl?.getBoundingClientRect().bottom ?? 0

  // Mobile sticky button bar shows below the `laptop` breakpoint — at `laptop`+ the sidebar
  // nav replaces it (see ContentWithStickyNavigationMobileMenu's `isAllowedBreakpoint`). Its
  // height comes from the --content-with-sticky-nav-button-height CSS var.
  const hasStickyButtonBar = window.matchMedia(`(max-width: ${parseInt(BREAKPOINTS.laptop) - 1}px)`).matches
  let stickyButtonHeight = 0
  if (hasStickyButtonBar) {
    const probe = document.createElement('div')
    probe.style.cssText =
      'position:fixed;visibility:hidden;pointer-events:none;top:0;left:0;height:var(--content-with-sticky-nav-button-height);width:1px'
    document.body.appendChild(probe)
    stickyButtonHeight = probe.getBoundingClientRect().height
    document.body.removeChild(probe)
  }

  return headerBottom + stickyButtonHeight
}

// Chrome height plus 20px of breathing room, used to land scrolled-to content below the chrome.
export const measureStickyNavHeaderOffset = (): number => {
  return measureStickyChromeHeight() + 20
}

// Compute the scroll position that lands an element's content (its title) `clearance` pixels
// below the viewport top — i.e. just below the fixed header chrome. Reads the element's
// computed `padding-top` so the math stays correct whether the target carries its own
// clearance padding (regular menu categories) or not (FeatureSpotlight, prepended first
// category). Same value drives click-to-scroll landing and the scroll-tick active-section
// threshold so all categories activate and land consistently regardless of CSS.
// A target's `scroll-margin-top` stops the scroll short by that much, for content that has its own
// sticky chrome over the target (the allergen table's column header).
export const getStickyNavScrollTarget = (el: HTMLElement, clearance: number): number => {
  const computed = getComputedStyle(el)
  const paddingTop = parseFloat(computed.paddingTop) || 0
  const scrollMarginTop = parseFloat(computed.scrollMarginTop) || 0
  return el.getBoundingClientRect().top + document.documentElement.scrollTop + paddingTop - scrollMarginTop - clearance
}

// Marks the sticky-nav content so a URL hash pointing inside it can clear the mobile category bar too.
export const STICKY_NAV_CONTENT_ATTRIBUTE = 'data-content-with-sticky-navigation'

// Scroll position for a URL-hash target on arrival. Content inside a sticky nav lands where its own nav
// clicks land it; anything else just clears the header. Measured from the page top, never `offsetTop`,
// which is relative to the nearest positioned ancestor and sent the menu's spotlight anchor back to ~0.
export const getHashScrollTarget = (el: HTMLElement): number => {
  if (el.closest(`[${STICKY_NAV_CONTENT_ATTRIBUTE}]`)) {
    return getStickyNavScrollTarget(el, measureStickyNavHeaderOffset())
  }

  const headerBottom = document.getElementById(NAV_ID)?.getBoundingClientRect().bottom ?? 0
  return getStickyNavScrollTarget(el, headerBottom + 20)
}

type ContentWithStickyNavigation = {
  className?: string
  children: React.ReactNode
  lineItems: ContentWithStickyNavigationLineItem[]
  contentGroupQuerySelector: string
  widerNav?: boolean
  bottomNavContent?: React.ReactNode
  sectionBackground: SectionBackground
}

// eslint-disable-next-line
const ContentWithStickyNavigation = ({
  className,
  lineItems,
  children,
  contentGroupQuerySelector,
  widerNav,
  bottomNavContent,
  sectionBackground,
}: ContentWithStickyNavigation) => {
  const { scroll, onScrollCallback } = useContext(ScrollContext)
  const [activeId, setActiveId] = useState<string>('')
  const { i18n } = useI18n()
  const resizeKey = useWindowResize()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const setStickyMobileNavContent = useStore(state => state.setStickyMobileNavContent)
  const removeStickyMobileNavById = useStore(state => state.removeStickyMobileNavById)
  const setStickyMobileNavActiveItemsById = useStore(state => state.setStickyMobileNavActiveItemsById)
  const stickyBannerIsOpen = useStore(state => state.stickyBannerIsOpen)
  const [id, setId] = useState<string>('')

  // Probe the chrome height once per [stickyBannerIsOpen, resizeKey] window. Both the click
  // handler and the per-tick scroll handler read this through a ref so neither path mutates
  // the DOM (appendChild/removeChild) on every event.
  const cachedHeaderOffsetRef = useRef(0)
  useEffect(() => {
    // `stickyBannerIsOpen` stays in the deps so the offset is re-measured when the banner
    // toggles (the rendered header height changes), even though the value is now read live.
    cachedHeaderOffsetRef.current = measureStickyNavHeaderOffset()
  }, [stickyBannerIsOpen, resizeKey])

  const handleNavigationClick = (id: string) => {
    const listElement = document.getElementById(id)
    if (!id || !scroll || !listElement) return
    if (listElement instanceof HTMLElement) {
      const offset = getStickyNavScrollTarget(listElement, cachedHeaderOffsetRef.current)
      scroll.scrollTo(offset + 1, { duration: 0.6 })
    }
  }

  useEffect(() => {
    setId(`id_${Math.random().toString().replace('.', '')}`)
  }, [])

  useEffect(() => {
    if (!id) return

    setTimeout(() => {
      setStickyMobileNavContent({
        items: lineItems,
        container: containerRef.current as HTMLElement,
        bottomNavContent,
        sectionBackground,
      })
    }, 10)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems, bottomNavContent, sectionBackground, id])

  useEffect(() => {
    return () => {
      removeStickyMobileNavById(id)

      onScrollCallback({
        key: `scroll_${id}`,
        remove: true,
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  useEffect(() => {
    if (!lineItems.length) return
    if (!scroll) return
    if (!id) return

    onScrollCallback({
      key: `scroll_${id}`,
      remove: true,
    })

    onScrollCallback({
      key: `scroll_${id}`,
      callback: e => {
        const items = document.querySelectorAll(contentGroupQuerySelector)
        const currentScroll = e.targetScroll

        let activeId = lineItems[0].id

        items.forEach(el => {
          if (el instanceof HTMLElement) {
            const offset = getStickyNavScrollTarget(el, cachedHeaderOffsetRef.current)
            if (currentScroll > offset) {
              activeId = el.id || ''
            }
          }
        })

        setStickyMobileNavActiveItemsById({
          id: id,
          activeId: activeId,
        })
        setActiveId(activeId)
      },
    })
  }, [scroll, lineItems, contentGroupQuerySelector, resizeKey, id, setStickyMobileNavActiveItemsById, onScrollCallback, stickyBannerIsOpen])

  if (!lineItems?.length || !children || !contentGroupQuerySelector) return null

  return (
    <div
      id={id}
      className={classnames(styles.ContentWithStickyNavigation, className, { [styles.widerNav]: widerNav })}
      ref={containerRef}
      {...{ [STICKY_NAV_CONTENT_ATTRIBUTE]: '' }}
    >
      <div className={styles.inner}>
        <div className={styles.grid}>
          <div className={styles.navigationSide}>
            <nav className={classnames(styles.navigation, { [styles.stickyBannerIsOpen]: stickyBannerIsOpen })}>
              <div
                className={styles.navigation__inner}
                data-lenis-prevent
              >
                <div className={styles.navigationItems}>
                  {lineItems.map((lineItem, i) => (
                    <div
                      className={classnames(styles.navigation__item, {
                        [styles.isActive]: activeId === lineItem.id,
                      })}
                      key={i}
                    >
                      <button
                        className={styles.navigation__button}
                        aria-label={i18n('goToCategory')}
                        onClick={() => {
                          handleNavigationClick(lineItem.id)
                        }}
                      >
                        <span className={styles.navigation__buttonText}>{lineItem.title}</span>
                        {lineItem.count && (
                          <span className={styles.navigation__buttonCount}>&nbsp;({lineItem.count})</span>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
                {bottomNavContent && <div>{bottomNavContent}</div>}
              </div>
            </nav>
          </div>
          <div className={styles.listSide}>{children}</div>
        </div>
      </div>
    </div>
  )
}

ContentWithStickyNavigation.displayName = 'ContentWithStickyNavigation'

export default ContentWithStickyNavigation
